import React, { useState } from 'react';
import { setDoc } from 'firebase/firestore';
import { auth } from './firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import { useUserContext } from './UserContext';
import { PLAN_CONFIG, ADDON_CONFIG, QUOTA_CYCLE_DAYS } from './planConfig';
import { addBonus } from './quotaManager';
import { resolveSubscriptionDoc } from './subscriptionDoc';

const PAID_PLANS = ['basic', 'pro', 'research'];

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserDetails, isSubscribed } = useUserContext();

  const getInitialPlan = () => {
    const p = new URLSearchParams(location.search).get('plan');
    return PAID_PLANS.includes(p) ? p : 'pro';
  };

  const [selectedPlan, setSelectedPlan] = useState(getInitialPlan());
  const [errorMessage, setErrorMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const currentUser = auth.currentUser;

  // ---- Razorpay helpers (shared by plans + add-ons) ----
  function getRzpPrefill() {
    const u = auth.currentUser;
    const name = u?.displayName || 'Tender Managers User';
    const email = u?.email || '';
    const contact = (u?.phoneNumber || '').replace(/[^\d+]/g, '');
    return { name, email, contact };
  }

  function getIOSSafariFix() {
    const ua = navigator.userAgent || '';
    const isIOS = /iP(hone|ad|od)/.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    return isIOS && isSafari ? { config: { upi: { flow: 'collect' } } } : {};
  }

  // ---- Plan purchase ----
  const handlePlanPurchase = async () => {
    if (!currentUser) { setErrorMessage('Please login first.'); return; }
    const cfg = PLAN_CONFIG[selectedPlan];
    if (!cfg) { setErrorMessage('Invalid plan.'); return; }

    setBusy(true);
    setErrorMessage('');

    try {
      const res = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: cfg.priceInPaise }),
      });
      const orderData = await res.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Tender Managers',
        description: `${cfg.label} Plan - Annual`,
        order_id: orderData.id,
        prefill: getRzpPrefill(),
        ...getIOSSafariFix(),
        handler: async (response) => {
          try {
            const verify = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyData = await verify.json();

            if (verifyData.status === 'success') {
              const phone = currentUser.phoneNumber;
              const { ref: docRef, snap: docSnap } = await resolveSubscriptionDoc(phone);

              const today = new Date();
              const durationMs = cfg.durationDays * 24 * 60 * 60 * 1000;

              // Extend from current endDate if still active, else from today
              let baseDate = today;
              if (docSnap.exists() && docSnap.data().endDate) {
                const raw = docSnap.data().endDate;
                const existing = raw.toDate ? raw.toDate() : raw instanceof Date ? raw : new Date(raw);
                if (existing.getTime() > today.getTime()) baseDate = existing;
              }

              const newEndDate = new Date(baseDate.getTime() + durationMs);
              const cycleEnd = new Date(today.getTime() + QUOTA_CYCLE_DAYS * 24 * 60 * 60 * 1000);

              await setDoc(docRef, {
                plan: selectedPlan,
                startDate: today,
                endDate: newEndDate,
                status: 'Active',
                quota: {
                  syncedPlan: selectedPlan,
                  cycleStart: today,
                  cycleEnd: cycleEnd,
                  searches: { used: 0, bonus: 0, limit: cfg.limits.searches },
                  results: { used: 0, bonus: 0, limit: cfg.limits.results },
                  tmBids: { used: 0, bonus: 0, limit: cfg.limits.tmBids },
                  reportRows: { used: 0, bonus: 0, limit: cfg.limits.reportRows },
                },
              }, { merge: true });

              try { await refreshUserDetails(); } catch (e) { console.error(e); }
              navigate('/tender-selection');
            } else {
              setErrorMessage('Payment verification failed. Please try again.');
            }
          } catch (err) {
            console.error('Payment handler error:', err);
            setErrorMessage('Error during payment processing.');
          }
        },
        theme: { color: '#1976d2' },
        modal: { ondismiss: () => { setBusy(false); setErrorMessage('Payment cancelled.'); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Order creation error:', err);
      setErrorMessage('Failed to start payment. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // ---- Add-on purchase ----
  const handleAddonPurchase = async (addonKey) => {
    if (!currentUser) { setErrorMessage('Please login first.'); return; }
    if (!isSubscribed) { setErrorMessage('You need an active plan before purchasing add-ons.'); return; }

    const addon = ADDON_CONFIG[addonKey];
    if (!addon) return;

    setBusy(true);
    setErrorMessage('');

    try {
      const res = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: addon.priceInPaise }),
      });
      const orderData = await res.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Tender Managers',
        description: `Add-on: ${addon.label}`,
        order_id: orderData.id,
        prefill: getRzpPrefill(),
        ...getIOSSafariFix(),
        handler: async (response) => {
          try {
            const verify = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyData = await verify.json();

            if (verifyData.status === 'success') {
              await addBonus(addon.quotaField, addon.amount);
              try { await refreshUserDetails(); } catch (e) { console.error(e); }
              setErrorMessage(''); // clear
              alert(`${addon.label} added successfully!`);
            } else {
              setErrorMessage('Add-on payment verification failed.');
            }
          } catch (err) {
            console.error(err);
            setErrorMessage('Error processing add-on payment.');
          }
        },
        theme: { color: '#1976d2' },
        modal: { ondismiss: () => { setBusy(false); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      setErrorMessage('Failed to start add-on payment.');
    } finally {
      setBusy(false);
    }
  };

  // ---- Render ----
  const cardStyle = (planId) => ({
    border: selectedPlan === planId ? '2px solid #1976d2' : '1px solid #e0e0e0',
    borderRadius: 12,
    padding: 20,
    cursor: 'pointer',
    background: selectedPlan === planId ? '#e3f2fd' : '#fff',
    flex: '1 1 220px',
    minWidth: 220,
    position: 'relative',
  });

  return (
    <Layout>
      <div style={{ maxWidth: 960, margin: '24px auto', padding: 16 }}>
        <h2>Choose Your Plan</h2>
        <p style={{ color: '#555', marginBottom: 24 }}>All plans are annual (365 days). Pick what suits your needs.</p>

        {/* Plan cards */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
          {PAID_PLANS.map((key) => {
            const cfg = PLAN_CONFIG[key];
            return (
              <div key={key} style={cardStyle(key)} onClick={() => setSelectedPlan(key)}>
                {key === 'pro' && (
                  <div style={{
                    position: 'absolute', top: -10, right: 16,
                    background: '#ff9800', color: '#fff', fontSize: 11, fontWeight: 700,
                    padding: '2px 10px', borderRadius: 12,
                  }}>POPULAR</div>
                )}
                <h3 style={{ margin: '0 0 8px' }}>{cfg.label}</h3>
                <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
                  {'\u20B9'}{cfg.priceInr.toLocaleString('en-IN')}
                  <span style={{ fontSize: 14, fontWeight: 400, color: '#777' }}> /year</span>
                </div>
                <ul style={{ paddingLeft: 18, fontSize: 14, color: '#444', lineHeight: 1.8 }}>
                  <li>{cfg.limits.searches.toLocaleString()} tender discovery credits/month</li>
                  <li>{cfg.limits.results.toLocaleString()} results analysis credits/month</li>
                  <li>{cfg.limits.tmBids} bid analyses/month</li>
                  <li>{cfg.limits.reportRows.toLocaleString()} report rows/month</li>
                </ul>
              </div>
            );
          })}
        </div>

        {/* Purchase button */}
        <button
          onClick={handlePlanPurchase}
          disabled={busy}
          style={{
            padding: '12px 32px', fontSize: 16, fontWeight: 600,
            background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8,
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
            marginBottom: 32,
          }}
        >
          {busy ? 'Processing...' : `Subscribe to ${PLAN_CONFIG[selectedPlan].label} - \u20B9${PLAN_CONFIG[selectedPlan].priceInr.toLocaleString('en-IN')}`}
        </button>

        {/* Add-ons (only for active subscribers) */}
        {isSubscribed && (
          <>
            <h3 style={{ marginTop: 32 }}>Need More? Purchase Add-ons</h3>
            <p style={{ color: '#555', marginBottom: 16 }}>Extra quota for your current billing cycle.</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {Object.values(ADDON_CONFIG).map((addon) => (
                <div key={addon.id} style={{
                  border: '1px solid #e0e0e0', borderRadius: 12, padding: 16,
                  flex: '1 1 200px', minWidth: 200, background: '#fafafa',
                }}>
                  <h4 style={{ margin: '0 0 8px' }}>{addon.label}</h4>
                  <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>
                    {'\u20B9'}{addon.priceInr}
                  </div>
                  <button
                    onClick={() => handleAddonPurchase(addon.id)}
                    disabled={busy}
                    style={{
                      padding: '8px 20px', background: '#43a047', color: '#fff',
                      border: 'none', borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer',
                      opacity: busy ? 0.6 : 1,
                    }}
                  >
                    Buy
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {errorMessage && <p style={{ color: 'red', marginTop: 16 }}>{errorMessage}</p>}
      </div>
    </Layout>
  );
};

export default SubscriptionPage;
